// /app/frontend/src/components/FollowupPhotosModal.jsx
// Sprint B-3 (Angela 2026-05-18) — Inspector uploads 2 mandatory photos
// before closing a followup. After upload, marks complete -> pending_review.
import { useState, useRef } from "react";
import axios from "axios";
import { Camera, CheckCircle, AlertTriangle, X, Upload } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function FollowupPhotosModal({ followup, onClose, onCompleted }) {
  const [errorPhotoUrl, setErrorPhotoUrl] = useState(followup?.error_photo_url || "");
  const [correctPhotoUrl, setCorrectPhotoUrl] = useState(followup?.correct_photo_url || "");
  const [uploading, setUploading] = useState({ error: false, correct: false });
  const [submitting, setSubmitting] = useState(false);
  const [err, setErr] = useState("");
  const errorFileRef = useRef(null);
  const correctFileRef = useRef(null);

  const upload = async (kind, file) => {
    if (!file) return;
    setUploading(u => ({ ...u, [kind]: true }));
    setErr("");
    try {
      const fd = new FormData();
      fd.append("kind", kind);
      fd.append("file", file);
      const res = await axios.post(
        `${API}/inspector/followups/${followup.id}/upload-photo`,
        fd,
        { headers: { "Content-Type": "multipart/form-data" } }
      );
      if (kind === "error") setErrorPhotoUrl(res.data.url);
      else setCorrectPhotoUrl(res.data.url);
    } catch (e) {
      setErr(e.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(u => ({ ...u, [kind]: false }));
    }
  };

  const submit = async () => {
    if (!errorPhotoUrl || !correctPhotoUrl) {
      setErr("Both photos required before closing.");
      return;
    }
    setSubmitting(true);
    setErr("");
    try {
      await axios.patch(`${API}/inspector/followups/${followup.id}/complete`);
      onCompleted?.();
      onClose();
    } catch (e) {
      setErr(e.response?.data?.detail || "Failed to complete");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-[1100] p-4" data-testid="followup-photos-modal">
      <div className="bg-white rounded-xl w-full max-w-md shadow-2xl flex flex-col max-h-[90vh]" onClick={e => e.stopPropagation()}>
        <div className="px-5 py-4 border-b border-gray-100 flex items-start justify-between">
          <div>
            <h2 className="text-base font-bold text-[#1F2937]">Close Follow-up — 2 photos required</h2>
            <p className="text-[10px] text-gray-500 mt-0.5">Order {followup.order_number}</p>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-700" data-testid="followup-photos-close">
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="p-5 space-y-4 overflow-y-auto">
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-xs text-amber-900 leading-relaxed">
            <strong>FOLLOWUP-001:</strong> Você deve enviar 2 fotos antes de fechar este followup:
            <br />1. Foto do <strong>erro</strong> (como estava antes)
            <br />2. Foto do <strong>correto</strong> (depois da correção)
          </div>

          {/* Error photo */}
          <div className="border border-red-200 rounded-lg p-3">
            <p className="text-xs font-bold text-red-700 mb-2 flex items-center gap-1.5">
              <Camera className="h-3.5 w-3.5" /> 1. Foto do ERRO *
            </p>
            {errorPhotoUrl ? (
              <div className="flex items-center gap-2">
                <img src={`${BACKEND_URL}${errorPhotoUrl}`} alt="error" className="h-16 w-16 object-cover rounded border" />
                <button onClick={() => errorFileRef.current?.click()} className="text-xs text-blue-600 underline" data-testid="followup-photo-error-replace">Substituir</button>
                <CheckCircle className="h-4 w-4 text-green-600 ml-auto" />
              </div>
            ) : (
              <button
                onClick={() => errorFileRef.current?.click()}
                disabled={uploading.error}
                className="w-full py-3 border-2 border-dashed border-red-300 rounded-lg text-red-700 text-xs font-semibold hover:bg-red-50 disabled:opacity-50"
                data-testid="followup-photo-error-upload"
              >
                {uploading.error ? "Enviando..." : <><Upload className="inline h-3 w-3 mr-1" />Enviar foto do erro</>}
              </button>
            )}
            <input ref={errorFileRef} type="file" accept="image/*" capture="environment" className="hidden" onChange={e => upload("error", e.target.files?.[0])} data-testid="followup-photo-error-input" />
          </div>

          {/* Correct photo */}
          <div className="border border-green-200 rounded-lg p-3">
            <p className="text-xs font-bold text-green-700 mb-2 flex items-center gap-1.5">
              <Camera className="h-3.5 w-3.5" /> 2. Foto do CORRETO *
            </p>
            {correctPhotoUrl ? (
              <div className="flex items-center gap-2">
                <img src={`${BACKEND_URL}${correctPhotoUrl}`} alt="correct" className="h-16 w-16 object-cover rounded border" />
                <button onClick={() => correctFileRef.current?.click()} className="text-xs text-blue-600 underline" data-testid="followup-photo-correct-replace">Substituir</button>
                <CheckCircle className="h-4 w-4 text-green-600 ml-auto" />
              </div>
            ) : (
              <button
                onClick={() => correctFileRef.current?.click()}
                disabled={uploading.correct}
                className="w-full py-3 border-2 border-dashed border-green-300 rounded-lg text-green-700 text-xs font-semibold hover:bg-green-50 disabled:opacity-50"
                data-testid="followup-photo-correct-upload"
              >
                {uploading.correct ? "Enviando..." : <><Upload className="inline h-3 w-3 mr-1" />Enviar foto do correto</>}
              </button>
            )}
            <input ref={correctFileRef} type="file" accept="image/*" capture="environment" className="hidden" onChange={e => upload("correct", e.target.files?.[0])} data-testid="followup-photo-correct-input" />
          </div>

          {err && (
            <div className="p-2.5 bg-red-50 border border-red-200 rounded-lg text-xs text-red-600 flex items-center gap-1.5" data-testid="followup-photos-error">
              <AlertTriangle className="h-3.5 w-3.5 flex-shrink-0" /> {err}
            </div>
          )}

          <div className="flex gap-2 pt-1">
            <button onClick={onClose} disabled={submitting} className="flex-1 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 text-sm font-semibold rounded-lg disabled:opacity-50" data-testid="followup-photos-cancel">
              Cancelar
            </button>
            <button
              onClick={submit}
              disabled={submitting || !errorPhotoUrl || !correctPhotoUrl}
              className="flex-1 py-2 bg-[#6B8F62] hover:bg-[#5a7a52] text-white text-sm font-bold rounded-lg disabled:bg-gray-300"
              data-testid="followup-photos-submit"
            >
              {submitting ? "Enviando..." : "Fechar Follow-up"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
